import { RequestService, type RequestOptions } from '../request-service';
import {
  SectionsPlacement,
  type GetSectionRequest,
  type GetSectionResponse,
} from '@wix/ambassador-appmarket-v1-section/types';
import type RpcModule from '@wix/ambassador-appmarket-v1-section/rpc';
import type HttpModule from '@wix/ambassador-appmarket-v1-section/http';
import { MarketSections } from './market-sections';
import type { AppBenefitSection, SidebarSection } from './types';

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

export class MarketSectionsService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  protected async rpcModule(): Promise<RpcModuleType> {
    return import('@wix/ambassador-appmarket-v1-section/rpc');
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import('@wix/ambassador-appmarket-v1-section/http');
  }

  public async getSidebarSections(
    {
      hasSiteContext,
    }: {
      hasSiteContext: boolean;
    },
    options?: RequestOptions,
  ): Promise<SidebarSection[]> {
    const response = await this.request<GetSectionRequest, GetSectionResponse>({
      methodName: 'getSection',
      payload: {
        placement: SectionsPlacement.SIDE_BAR,
        customParams: {
          isMetaSiteId: JSON.stringify(hasSiteContext),
        },
      },
      options,
    });

    const marketSections = new MarketSections(response.sections);
    return marketSections.toSidebarSections();
  }

  public async getAppBenefitsSections(
    options?: RequestOptions,
  ): Promise<AppBenefitSection[]> {
    const response = await this.request<GetSectionRequest, GetSectionResponse>({
      methodName: 'getSection',
      payload: {
        placement: SectionsPlacement.APP_BENEFITS,
        customParams: {
          LineItemsAsFamily: 'any',
        },
      },
      options,
    });

    const marketSections = new MarketSections(response.sections);
    return marketSections.toAppBenefitSections();
  }

  public async getPageSections(
    {
      placement,
      hasSiteContext,
      relatedTo,
      layout,
    }: {
      placement:
        | 'HOME_PAGE'
        | 'EDITOR_3_APPS_PAGE'
        | 'EDITOR_3_RECOMMENDED_APPS'
        | 'EDITOR_3_RELATED_APPS'
        | 'BM_HOME_PACKAGES';
      hasSiteContext: boolean;
      relatedTo?: string;
      layout?: string;
    },
    options?: RequestOptions,
  ) {
    const response = await this.request<GetSectionRequest, GetSectionResponse>({
      methodName: 'getSection',
      payload: {
        placement:
          placement === 'HOME_PAGE'
            ? SectionsPlacement.PAGE
            : placement === 'EDITOR_3_APPS_PAGE'
            ? SectionsPlacement.EDITOR_3_APPS
            : placement === 'EDITOR_3_RECOMMENDED_APPS'
            ? SectionsPlacement.EDITOR_3_RECOMMENDED_APPS
            : placement === 'EDITOR_3_RELATED_APPS'
            ? SectionsPlacement.EDITOR_3_RELATED_APPS
            : SectionsPlacement.BM_HOME_PACKAGES,
        customParams: {
          isMetaSiteId: JSON.stringify(hasSiteContext),
          ...(relatedTo && { relatedTo }),
          ...(layout && { layout }),
        },
      },
      options,
    });

    const marketSections = new MarketSections(response.sections);
    return marketSections.toPageSections();
  }
}
